/**
 * Economic calendar: categories, the event guides each release opens (econ-guides.ts), matching a
 * source's event title to its guide, and the number handling behind the Surprise column. Shared by
 * the API (storing and serving events, key-event alerts) and the web app (calendar page and guides).
 */
import { ECON_GUIDES } from './econ-guides.js';
export const ECON_CATEGORIES = [
    { id: 'central_banks', label: 'Central banks' },
    { id: 'speeches', label: 'Speeches' },
    { id: 'inflation', label: 'Inflation' },
    { id: 'jobs', label: 'Jobs' },
    { id: 'growth', label: 'Growth' },
    { id: 'business', label: 'Business surveys' },
    { id: 'consumer', label: 'Consumers' },
    { id: 'housing', label: 'Housing' },
    { id: 'trade', label: 'Trade' },
    { id: 'energy', label: 'Energy' },
    { id: 'bonds', label: 'Bond auctions' },
    { id: 'holidays', label: 'Holidays' },
    { id: 'other', label: 'Other' },
];
export const ECON_CATEGORY_LABEL = Object.fromEntries(ECON_CATEGORIES.map((c) => [c.id, c.label]));
/** Currencies the calendar covers, in the order the filter lists them. */
export const ECON_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'NZD', 'CHF', 'CNY'];
/** Currency → the country code the rest of the platform uses for it (flags, `exposedSymbols`). */
export const ECON_CCY_COUNTRY = { USD: 'US', EUR: 'EU', GBP: 'GB', JPY: 'JP', CAD: 'CA', AUD: 'AU', NZD: 'NZ', CHF: 'CH', CNY: 'CN' };
export const ECON_GUIDE_BY_SLUG = Object.fromEntries(ECON_GUIDES.map((g) => [g.slug, g]));
/** Category guide for each category. */
export const CATEGORY_GUIDE = {
    central_banks: 'central-bank-decisions',
    speeches: 'central-bank-speeches',
    inflation: 'inflation-data',
    jobs: 'jobs-data',
    growth: 'growth-data',
    business: 'business-surveys',
    consumer: 'consumer-data',
    housing: 'housing-data',
    trade: 'trade-data',
    energy: 'energy-data',
    bonds: 'bond-auctions',
    holidays: 'market-holidays',
    other: 'other-releases',
};
/** Category of an event without a guide of its own, from its title. First match wins. */
const CATEGORY_RULES = [
    [/holiday/i, 'holidays'],
    [/\bspeaks\b|speech|testifies|testimony|press conference|\bsummit\b|\bmeetings?\b/i, 'speeches'],
    [/rate decision|policy rate|cash rate|bank rate|funds rate|refinancing rate|deposit (facility )?rate|overnight rate|monetary policy|rate statement|minutes|mpc|fomc|boj|ecb|snb|rba|rbnz|boc\b/i, 'central_banks'],
    [/\bcpi\b|\bppi\b|\bpce\b|inflation|price index|prices\b|\bhicp\b|deflator/i, 'inflation'],
    [/employment|unemployment|payrolls|jobless|claims|claimant|jobs|earnings|wage|labou?r|jolts/i, 'jobs'],
    [/crude|oil|natural gas|gasoline|distillate|petroleum|opec|rig count|\beia\b|\bapi\b weekly/i, 'energy'],
    [/\bgdp\b|growth|industrial production|manufacturing production|output|factory orders|durable goods|capital spending|business investment/i, 'growth'],
    [/\bpmi\b|\bism\b|ifo|zew|sentix|tankan|business (confidence|climate|sentiment|survey)|empire state|philly|richmond|chicago pmi|kof/i, 'business'],
    [/retail|consumer|sentiment|confidence|spending|household|personal income|car sales|uom\b/i, 'consumer'],
    [/housing|home sales|building permits|house price|mortgage|construction|starts\b|nahb|hpi\b/i, 'housing'],
    [/trade balance|current account|exports|imports|tic\b/i, 'trade'],
    [/auction|bond|bill\b|gilt|treasury|t-note|bund/i, 'bonds'],
];
export function categoryOf(title) {
    for (const [re, cat] of CATEGORY_RULES)
        if (re.test(title))
            return cat;
    return 'other';
}
/** The guide an event opens: its own when one matches its title and currency, else its category's. */
export function econGuideFor(title, currency) {
    const ccy = currency.toUpperCase();
    // "BOE Gov Bailey Speaks" names the bank but is a speech: only speech guides may claim it.
    const speech = /\bspeaks\b|testif|speech/i.test(title);
    for (const g of ECON_GUIDES) {
        if (!g.match || (g.currency !== null && g.currency !== ccy) || (speech && g.category !== 'speeches'))
            continue;
        if (g.match.test(title))
            return g;
    }
    return ECON_GUIDE_BY_SLUG[CATEGORY_GUIDE[categoryOf(title)]];
}
/** Stable key of a recurring release ("every crude oil inventories report"): currency and normalised title. */
export function econSeries(currency, title) {
    return `${currency.toUpperCase()}:${title
        .toLowerCase()
        .replace(/[^a-z0-9/%]+/g, '-')
        .replace(/^-|-$/g, '')}`.slice(0, 120);
}
/** Stable id of one release. */
export function econEventId(currency, time, title) {
    return `${econSeries(currency, title)}@${time.slice(0, 16)}`.slice(0, 160);
}
const SUFFIX = { K: 1e3, M: 1e6, B: 1e9, T: 1e12 };
/** "-1.5M" → -1500000, "54.2" → 54.2, "3.75%" → 3.75, "<0.1%" → 0.1; null when it isn't a number. */
export function parseEconNumber(s) {
    if (s === null || s === undefined)
        return null;
    const m = String(s)
        .replace(/[,<>\s]/g, '')
        .replace(/[−–]/g, '-')
        .match(/^([+-]?\d*\.?\d+)([KMBT])?%?$/i);
    if (!m)
        return null;
    const n = Number(m[1]) * (m[2] ? SUFFIX[m[2].toUpperCase()] : 1);
    return Number.isFinite(n) ? n : null;
}
/** How the actual figure compares with the forecast; null until both are known numbers. */
export function econSurprise(actual, forecast, previous, good) {
    const a = parseEconNumber(actual);
    const f = parseEconNumber(forecast);
    if (a === null || f === null)
        return null;
    const diff = a - f;
    if (Math.abs(diff) < 1e-9)
        return { diff: 0, size: 0, verdict: 'inline' };
    const p = parseEconNumber(previous);
    const scale = Math.max(Math.abs(f), Math.abs(p ?? 0), Math.abs(a), 1e-9);
    const size = Math.max(-1, Math.min(1, diff / scale));
    const up = diff > 0;
    const verdict = good === null ? (up ? 'above' : 'below') : up === (good === 'higher') ? 'beat' : 'miss';
    return { diff, size, verdict };
}
export const ECON_VERDICT_LABEL = { beat: 'Beat', miss: 'Miss', above: 'Above', below: 'Below', inline: 'As expected' };
//# sourceMappingURL=econ-calendar.js.map